<template>
  <ContentWrap>
    <el-tabs v-model="activeSlot" @tab-change="onSlotChange">
      <el-tab-pane label="快捷金刚" :name="String(HOME_ENTRY_SLOT.QUICK)" />
      <el-tab-pane label="营销三入口" :name="String(HOME_ENTRY_SLOT.PROMO)" />
    </el-tabs>

    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="80px"
    >
      <el-form-item label="标题" prop="title">
        <el-input
          v-model="queryParams.title"
          class="!w-200px"
          clearable
          placeholder="标题"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select v-model="queryParams.status" class="!w-160px" clearable placeholder="全部">
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['promotion:home-entry:create']"
          plain
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-alert
      v-if="Number(activeSlot) === HOME_ENTRY_SLOT.PROMO"
      class="mb-12px"
      type="info"
      :closable="false"
      title="C 端只取启用且排序靠前的 3 条：第 1 条为大卡，第 2、3 条为右侧两格。跳转选填。"
    />
    <el-alert
      v-else
      class="mb-12px"
      type="info"
      :closable="false"
      title="C 端按排序展示，固定 4 列折行。图标与跳转均可选填；无启用数据时回落默认五项。"
    />
    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <el-table-column align="center" label="标题" prop="title" min-width="100" />
      <el-table-column
        v-if="Number(activeSlot) === HOME_ENTRY_SLOT.PROMO"
        align="center"
        label="副标题"
        prop="subtitle"
        min-width="100"
      />
      <el-table-column align="center" label="图标" min-width="70" prop="iconUrl">
        <template #default="{ row }">
          <el-image
            v-if="row.iconUrl"
            :src="row.iconUrl"
            class="h-36px w-36px"
            @click="imagePreview(row.iconUrl)"
          />
          <span v-else class="text-[var(--el-text-color-secondary)]">默认</span>
        </template>
      </el-table-column>
      <el-table-column
        v-if="Number(activeSlot) === HOME_ENTRY_SLOT.QUICK"
        align="center"
        label="角标"
        prop="badgeText"
        width="80"
      />
      <el-table-column align="center" label="跳转" prop="url" min-width="140" />
      <el-table-column align="center" label="排序" prop="sort" width="70" />
      <el-table-column align="center" label="状态" prop="status" width="90">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.COMMON_STATUS" :value="scope.row.status" />
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="170"
      />
      <el-table-column align="center" label="操作" width="140" fixed="right">
        <template #default="scope">
          <el-button
            v-hasPermi="['promotion:home-entry:update']"
            link
            type="primary"
            @click="openForm('update', scope.row.id)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['promotion:home-entry:delete']"
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <HomeEntryForm ref="formRef" @success="getList" />
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import * as HomeEntryApi from '@/api/mall/market/homeEntry'
import { HOME_ENTRY_SLOT } from '@/api/mall/market/homeEntry'
import HomeEntryForm from './HomeEntryForm.vue'
import { createImageViewer } from '@/components/ImageViewer'

defineOptions({ name: 'PromotionHomeEntry' })

const message = useMessage()
const { t } = useI18n()

const activeSlot = ref(String(HOME_ENTRY_SLOT.QUICK))
const loading = ref(true)
const total = ref(0)
const list = ref([])
const queryParams = reactive({
  pageNo: 1,
  pageSize: 20,
  title: null as string | null,
  status: null as number | null,
  slot: HOME_ENTRY_SLOT.QUICK as number
})
const queryFormRef = ref()

const imagePreview = (imgUrl: string) => {
  createImageViewer({ urlList: [imgUrl] })
}

const getList = async () => {
  loading.value = true
  queryParams.slot = Number(activeSlot.value)
  try {
    const data = await HomeEntryApi.getHomeEntryPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const onSlotChange = () => {
  queryParams.pageNo = 1
  getList()
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const formRef = ref()
const openForm = (type: string, id?: number) => {
  formRef.value.open(type, Number(activeSlot.value), id)
}

const handleDelete = async (id: number) => {
  try {
    await message.delConfirm()
    await HomeEntryApi.deleteHomeEntry(id)
    message.success(t('common.delSuccess'))
    await getList()
  } catch {}
}

onMounted(() => {
  getList()
})
</script>
